import { CompactUnitSelect } from "./CompactUnitSelect";
import React, { Fragment, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  alpha,
  Fade,
  Divider,
  Skeleton,
} from "@mui/material";
import dayjs from "dayjs";
import { inventoryService } from "../../../../services/inventory.service";
import { convertUnits } from "../../../../utils/units";
import { toast } from "react-toastify";
import { isNull } from "lodash";
import { IESDoc, ITableFilters } from "../../../../../../app.interface";
import { IngredientDialog } from "../../Recipes/components/IngredientDialog";
import { IngredientOption, ExtendedBatch } from "../../Recipes/types";

interface IngredientsStepProps {
  fullDocumnet: any;
  selectedDocument: any;
  selectedRecipe: any;
  handleUpdateFullDoc: (value: any, field: string) => void;
  modifiedIngredients: any;
  setModifiedIngredients: any;
  setCanProceed: (canProceed: boolean) => void;
}

interface Ingredient {
  _id: string;
  ingredient_id: string;
  name: string;
  description: string;
  category: string;
  unit: string;
  base_unit: string;
  stock_quantity: number;
  cost_per_unit: number;
  type: string;
}

interface Batch {
  _id: string;
  batch_number: string;
  quantity: number;
  remaining_quantity: number;
  unit: string;
  cost_per_unit: number;
  expiry_date: number;
  brand_name: string;
  product_id: string;
}

interface ModifiedIngredient {
  item_id: string;
  ingredient_id: string;
  brand_name: string;
  modified_quantity: number;
  original_quantity: number;
  unit: string;
  selected_batches: SelectedBatch[];
  is_modified: boolean;
  required_quantity: number;
  remaining_quantity: number;
  batches: ExtendedBatch[];
}

interface SelectedBatch {
  id: string;
  quantity: number;
  unit: string;
  cost_per_unit: number;
  total_ingredient_batch_cost: number;
}

interface BatchInfo {
  batches: ExtendedBatch[];
  totalCost: number;
  averageCost: number;
  totalQuantity: number;
  lastFetched: number;
}

export const IngredientsStep: React.FC<IngredientsStepProps> = ({
  fullDocumnet,
  selectedDocument,
  selectedRecipe,
  handleUpdateFullDoc,
  modifiedIngredients,
  setModifiedIngredients,
  setCanProceed,
}) => {
  const isViewMode = !isNull(selectedDocument);
  const theme = useTheme();
  const [ingredientsLoading, setIngredientsLoading] = useState(false);
  const [expandedIngredients, setExpandedIngredients] = useState<boolean[]>([]);
  const [autoAllocating, setAutoAllocating] = useState<boolean>(false);
  const [multiplier, setMultiplier] = useState<number>(1);
  const [editingIngredientIndex, setEditingIngredientIndex] = useState<
    number | null
  >(null);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [ingredientOptions, setIngredientOptions] = useState<
    IngredientOption[]
  >([]);
  const [loadingIngredients, setLoadingIngredients] = useState(false);
  const [batchDataCache, setBatchDataCache] = useState<
    Record<string, BatchInfo>
  >({});

  // Loading skeleton component
  const BatchSkeleton = () => (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        bgcolor: "white",
        borderColor: alpha(theme.palette.grey[300], 0.8),
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Stack spacing={1}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="40%" height={20} />
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack spacing={1}>
            <Skeleton variant="text" width="50%" height={20} />
            <Skeleton
              variant="rectangular"
              height={16}
              sx={{ borderRadius: 4 }}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack spacing={1}>
            <Skeleton variant="text" width="70%" height={20} />
            <Skeleton
              variant="rectangular"
              height={40}
              sx={{ borderRadius: 1 }}
            />
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );

  // Ingredient skeleton component
  const IngredientSkeleton = () => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        bgcolor: "white",
        borderRadius: 2,
        border: "1px solid",
        borderColor: alpha(theme.palette.grey[300], 0.8),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Skeleton
          variant="rectangular"
          width={40}
          height={40}
          sx={{ borderRadius: 1 }}
        />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="40%" height={24} />
          <Skeleton variant="text" width="30%" height={20} />
        </Box>
        <Skeleton variant="circular" width={32} height={32} />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Skeleton
            variant="rectangular"
            height={56}
            sx={{ borderRadius: 1 }}
          />
        </Grid>
      </Grid>
    </Paper>
  );

  const ViewModeMessage = () => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        bgcolor: alpha(theme.palette.info.light, 0.1),
        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Icon sx={{ color: theme.palette.info.main }}>info</Icon>
      <Typography variant="body2" sx={{ color: theme.palette.info.main }}>
        You are viewing the ingredients and batches used in this processed item.
        This information cannot be modified.
      </Typography>
    </Paper>
  );

  const fetchIngredientsAndBatches = () => {
    if (isViewMode) {
      // View mode loading
      setIngredientsLoading(true);
      const ingredientIds = selectedDocument.ingredients.map(
        (ingredient: any) => ingredient.ingredient_id
      );

      const IngredientsFilters: ITableFilters[] = ingredientIds.map(
        (ingredientId: string) => ({
          id: ingredientId,
          title: "ingredient_id",
          field: "ingredient_id",
          operator: "is one of",
          value: ingredientId,
        })
      );

      inventoryService
        .listIngredients({
          pageIndex: 0,
          pageSize: 100,
          sortField: "created_at",
          sortOrder: "desc",
          filters: IngredientsFilters,
        })
        .then((res) => {
          const fetchedIngredients = res.data;
          const initialModifiedIngredients = fetchedIngredients.map(
            (ingredient) => {
              const docIngredient = selectedDocument.ingredients.find(
                (i: any) => i.ingredient_id === ingredient.ingredient_id
              );

              return {
                item_id: ingredient._id,
                ingredient_id: ingredient.ingredient_id,
                brand_name: ingredient.name,
                modified_quantity: docIngredient?.quantity || 0,
                original_quantity: docIngredient?.quantity || 0,
                unit: docIngredient?.unit || ingredient.unit,
                selected_batches: [
                  {
                    id: docIngredient?.batch_id || "",
                    quantity: docIngredient?.quantity || 0,
                    unit: docIngredient?.unit || ingredient.unit,
                    cost_per_unit:
                      docIngredient?.cost / docIngredient?.quantity || 0,
                    total_ingredient_batch_cost: docIngredient?.cost || 0,
                  },
                ],
                is_modified: false,
                required_quantity: docIngredient?.quantity || 0,
                remaining_quantity: 0,
                batches: [],
              };
            }
          );

          setModifiedIngredients(initialModifiedIngredients);
          setExpandedIngredients(
            new Array(fetchedIngredients.length).fill(true)
          );
        })
        .catch(() => {
          toast.error("Error loading ingredients");
        })
        .finally(() => {
          setIngredientsLoading(false);
        });
    } else {
      // Add mode loading
      setIngredientsLoading(true);
      const recipeIngredients = selectedRecipe?.ingredients || [];
      const ingredientIds = recipeIngredients.map(
        (ingredient: any) => ingredient.ingredient_id
      );

      const IngredientsFilters: ITableFilters[] = ingredientIds.map(
        (ingredientId: string) => ({
          id: ingredientId,
          title: "ingredient_id",
          field: "ingredient_id",
          operator: "is one of",
          value: ingredientId,
        })
      );

      inventoryService
        .listIngredients({
          pageIndex: 0,
          pageSize: 100,
          sortField: "created_at",
          sortOrder: "desc",
          filters: IngredientsFilters,
        })
        .then(async (res) => {
          const fetchedIngredients = res.data;
          const initialModifiedIngredients = await Promise.all(
            fetchedIngredients.map(async (ingredient) => {
              const recipeIngredient = recipeIngredients.find(
                (i: any) => i.ingredient_id === ingredient.ingredient_id
              );

              // Fetch batches for this ingredient
              const batchData = await getBatchData(ingredient.ingredient_id);
              const batches = batchData?.batches || [];

              return {
                item_id: ingredient._id,
                ingredient_id: ingredient.ingredient_id,
                brand_name: ingredient.name,
                modified_quantity: recipeIngredient?.quantity || 0,
                original_quantity: recipeIngredient?.quantity || 0,
                unit: recipeIngredient?.unit || ingredient.unit,
                selected_batches: [],
                is_modified: false,
                required_quantity: recipeIngredient?.quantity || 0,
                remaining_quantity: recipeIngredient?.quantity || 0,
                batches: batches,
              };
            })
          );

          setModifiedIngredients(initialModifiedIngredients);
          setExpandedIngredients(
            new Array(fetchedIngredients.length).fill(true)
          );
        })
        .catch((error) => {
          console.error("Error loading ingredients:", error);
          toast.error("Error loading ingredients");
        })
        .finally(() => {
          setIngredientsLoading(false);
        });
    }
  };

  useEffect(() => {
    fetchIngredientsAndBatches();
  }, [selectedDocument]);

  const isQuantityModified = (ingredient: ModifiedIngredient) => {
    // Consider a small difference threshold due to floating point arithmetic
    const threshold = 0.0001;
    return (
      Math.abs(ingredient.modified_quantity - ingredient.original_quantity) >
      threshold
    );
  };

  const handleExpandIngredient = (index: number) => {
    setExpandedIngredients((prev) => {
      const newState = [...prev];
      newState[index] = !newState[index];
      return newState;
    });
  };

  const handleIngredientQuantityChange = (index: number, value: number) => {
    setModifiedIngredients((prev: ModifiedIngredient[]) => {
      const newState = prev.map((ingredient, i) =>
        i === index
          ? {
              ...ingredient,
              modified_quantity: value,
              required_quantity: value,
              remaining_quantity:
                value -
                ingredient.selected_batches.reduce((total, batch) => {
                  const convertedQuantity =
                    batch.unit !== ingredient.unit
                      ? convertUnits(
                          batch.quantity,
                          batch.unit,
                          ingredient.unit
                        ) || batch.quantity
                      : batch.quantity;
                  return total + convertedQuantity;
                }, 0),
              is_modified: value !== ingredient.original_quantity * multiplier,
            }
          : ingredient
      );
      handleIngredientsUpdate(newState);
      return newState;
    });
  };

  const handleUnitChange = (index: number, newUnit: string) => {
    const ingredient = modifiedIngredients[index];
    const convertedQuantity = convertUnits(
      ingredient.modified_quantity,
      ingredient.unit,
      newUnit
    );
    if (convertedQuantity !== null) {
      handleIngredientQuantityChange(index, convertedQuantity);

      // Update the unit
      setModifiedIngredients((prev: ModifiedIngredient[]) =>
        prev.map((ing, i) => (i === index ? { ...ing, unit: newUnit } : ing))
      );
    }
  };

  const handleBatchSelect = (
    ingredientIndex: number,
    batchId: string,
    quantity: number,
    unit: string
  ) => {
    setModifiedIngredients((prev: ModifiedIngredient[]) => {
      // Deep clone the previous state
      const newState = JSON.parse(JSON.stringify(prev));
      const ingredient = newState[ingredientIndex];

      const existingBatchIndex = ingredient.selected_batches.findIndex(
        (b: SelectedBatch) => b.id === batchId
      );

      // Find the original batch
      const originalBatch = ingredient.batches.find(
        (b: ExtendedBatch) => b.batch_id === batchId
      );
      if (!originalBatch) return newState;

      // Convert quantities to the same unit for comparison
      const requestedQuantityInBatchUnit =
        unit !== originalBatch.unit
          ? convertUnits(quantity, unit, originalBatch.unit) || quantity
          : quantity;

      // Check if requested quantity exceeds available quantity
      if (requestedQuantityInBatchUnit > originalBatch.remaining_quantity) {
        toast.error(
          `Cannot select more than ${originalBatch.remaining_quantity} ${originalBatch.unit} from this batch`
        );
        return newState;
      }

      // Create a new selected_batches array
      let newSelectedBatches: SelectedBatch[];
      if (existingBatchIndex >= 0) {
        // Update existing batch
        if (quantity <= 0) {
          // Remove batch if quantity is 0 or negative
          newSelectedBatches = ingredient.selected_batches.filter(
            (b: SelectedBatch) => b.id !== batchId
          );
        } else {
          newSelectedBatches = ingredient.selected_batches.map(
            (b: SelectedBatch) =>
              b.id === batchId
                ? {
                    ...b,
                    id: batchId,
                    batch_id: batchId,
                    quantity,
                    unit,
                    total_ingredient_batch_cost:
                      quantity * originalBatch.cost_per_unit,
                  }
                : b
          );
        }
      } else {
        // Add new batch
        if (quantity > 0) {
          newSelectedBatches = [
            ...ingredient.selected_batches,
            {
              id: batchId,
              batch_id: batchId,
              quantity,
              unit,
              cost_per_unit: originalBatch.cost_per_unit,
              total_ingredient_batch_cost:
                quantity * originalBatch.cost_per_unit,
            },
          ];
        } else {
          newSelectedBatches = [...ingredient.selected_batches];
        }
      }

      // Calculate total selected quantity in ingredient's unit
      const totalSelectedQuantity = newSelectedBatches.reduce(
        (total: number, batch: SelectedBatch) => {
          const convertedQuantity =
            batch.unit !== ingredient.unit
              ? convertUnits(batch.quantity, batch.unit, ingredient.unit) ||
                batch.quantity
              : batch.quantity;
          return total + convertedQuantity;
        },
        0
      );

      // Update remaining quantities in batches
      const updatedBatches = ingredient.batches.map((batch: ExtendedBatch) => {
        if (batch.batch_id === batchId) {
          const currentSelectedBatch = newSelectedBatches.find(
            (b: SelectedBatch) => b.id === batchId
          );
          const currentQuantityInBatchUnit = currentSelectedBatch
            ? currentSelectedBatch.unit !== batch.unit
              ? convertUnits(
                  currentSelectedBatch.quantity,
                  currentSelectedBatch.unit,
                  batch.unit
                ) || currentSelectedBatch.quantity
              : currentSelectedBatch.quantity
            : 0;

          const previousSelectedBatch = ingredient.selected_batches.find(
            (b: SelectedBatch) => b.id === batchId
          );
          const previousQuantityInBatchUnit = previousSelectedBatch
            ? previousSelectedBatch.unit !== batch.unit
              ? convertUnits(
                  previousSelectedBatch.quantity,
                  previousSelectedBatch.unit,
                  batch.unit
                ) || previousSelectedBatch.quantity
              : previousSelectedBatch.quantity
            : 0;

          // Calculate the difference in quantity and update remaining_quantity
          const quantityDifference =
            currentQuantityInBatchUnit - previousQuantityInBatchUnit;
          return {
            ...batch,
            remaining_quantity: batch.remaining_quantity - quantityDifference,
          };
        }
        return batch;
      });

      // Update the ingredient
      ingredient.selected_batches = newSelectedBatches;
      ingredient.batches = updatedBatches;
      ingredient.remaining_quantity =
        ingredient.required_quantity - totalSelectedQuantity;
      ingredient.is_modified =
        totalSelectedQuantity !== ingredient.required_quantity;

      return newState;
    });
  };

  const handleBatchUnitChange = (
    ingredientIndex: number,
    batchId: string,
    currentQuantity: number,
    currentUnit: string,
    newUnit: string
  ) => {
    const convertedQuantity = convertUnits(
      currentQuantity,
      currentUnit,
      newUnit
    );
    if (convertedQuantity !== null) {
      handleBatchSelect(ingredientIndex, batchId, convertedQuantity, newUnit);
    }
  };

  const autoAllocateBatches = (
    ingredientIndex: number,
    ingredient: ModifiedIngredient
  ) => {
    // Clear existing allocations first
    ingredient.selected_batches.forEach((batch) => {
      handleBatchSelect(ingredientIndex, batch.id, 0, batch.unit);
    });

    // Sort batches by expiry date and remaining quantity (prioritize closer expiry dates and fuller batches)
    const sortedBatches = [...(ingredient.batches || [])].sort((a, b) => {
      const expiryDiff =
        dayjs(a.expiry_date).valueOf() - dayjs(b.expiry_date).valueOf();
      if (expiryDiff === 0) {
        return b.remaining_quantity - a.remaining_quantity;
      }
      return expiryDiff;
    });

    let remainingToAllocate = ingredient.required_quantity;
    const targetUnit = ingredient.unit;

    // Try to allocate from each batch
    sortedBatches.forEach((batch) => {
      if (remainingToAllocate <= 0 || !batch.remaining_quantity) return;

      // Convert batch's remaining quantity to ingredient's unit
      const batchQuantityInTargetUnit = convertUnits(
        batch.remaining_quantity,
        batch.unit,
        targetUnit
      );

      if (batchQuantityInTargetUnit === null) {
        console.warn(
          `Unable to convert units from ${batch.unit} to ${targetUnit}`
        );
        return;
      }

      // Determine how much we can allocate from this batch
      const allocateFromBatch = Math.min(
        remainingToAllocate,
        batchQuantityInTargetUnit
      );

      if (allocateFromBatch > 0) {
        // Convert back to batch's unit for storage
        const allocatedQuantityInBatchUnit = convertUnits(
          allocateFromBatch,
          targetUnit,
          batch.unit
        );

        if (allocatedQuantityInBatchUnit !== null) {
          // Allocate from this batch
          handleBatchSelect(
            ingredientIndex,
            batch.batch_id,
            allocatedQuantityInBatchUnit,
            batch.unit
          );

          remainingToAllocate -= allocateFromBatch;
        }
      }
    });

    // If we couldn't allocate everything, show a warning
    if (remainingToAllocate > 0) {
      toast.warning(
        `Could not fully allocate required quantity. ${remainingToAllocate} ${targetUnit} remaining.`
      );
    }
  };

  const handleAutoAllocateAll = async () => {
    setAutoAllocating(true);
    try {
      for (let i = 0; i < modifiedIngredients.length; i++) {
        const ingredient = modifiedIngredients[i];
        if (ingredient.batches && ingredient.batches.length > 0) {
          autoAllocateBatches(i, ingredient);
        }
      }
    } finally {
      setAutoAllocating(false);
    }
  };

  const handleMultiplierChange = (value: number) => {
    setMultiplier(value);
    // Update all ingredient quantities based on the new multiplier
    setModifiedIngredients((prev: ModifiedIngredient[]) =>
      prev.map((ingredient) => ({
        ...ingredient,
        modified_quantity: ingredient.original_quantity * value,
        required_quantity: ingredient.original_quantity * value,
        remaining_quantity: ingredient.original_quantity * value,
        selected_batches: ingredient.selected_batches.map((batch) => ({
          ...batch,
          quantity: (batch.quantity / (multiplier || 1)) * value,
        })),
      }))
    );
  };

  const handleRemoveIngredient = (index: number) => {
    setModifiedIngredients((prev: ModifiedIngredient[]) =>
      prev.filter((_, i) => i !== index)
    );
  };

  const handleLoadBatches = async (ingredientId: string) => {
    try {
      const response = await inventoryService.listBatches({
        ingredient_id: ingredientId,
        pageIndex: 0,
        pageSize: 100,
        sortField: "expiry_date",
        sortOrder: "asc",
        include_wasted: false,
      });
      return response;
    } catch (error) {
      console.error("Error loading batches:", error);
      return null;
    }
  };

  const handleOpenAddDialog = () => {
    setEditingIngredientIndex(null);
    setItemDialogOpen(true);
  };

  const handleEditIngredient = (index: number) => {
    setEditingIngredientIndex(index);
    setItemDialogOpen(true);
  };

  const loadIngredients = async () => {
    try {
      setLoadingIngredients(true);
      const response = await inventoryService.listIngredients({
        pageIndex: 0,
        pageSize: 100,
        sortField: "created_at",
        sortOrder: "desc",
      });

      const ingredients: IngredientOption[] = response.data.map(
        (item: any) => ({
          id: item.ingredient_id,
          _id: item._id,
          ingredient_id: item.ingredient_id,
          name: item.name,
          unit: item.unit,
          type: "ingredient" as "ingredient" | "supply",
          description: item.description,
          stock_quantity: item.stock_quantity,
        })
      );

      setIngredientOptions(ingredients);
    } catch (error) {
      console.error("Error loading ingredients:", error);
      toast.error("Failed to load ingredients");
    } finally {
      setLoadingIngredients(false);
    }
  };

  const getBatchData = React.useCallback(
    async (ingredientId: string) => {
      const cachedData = batchDataCache[ingredientId];
      const now = Date.now();
      if (cachedData && now - cachedData.lastFetched < 5 * 60 * 1000) {
        return {
          batches: cachedData.batches,
          totalCost: cachedData.totalCost,
          averageCost: cachedData.averageCost,
          totalQuantity: cachedData.totalQuantity,
        };
      }

      try {
        const response = await inventoryService.listBatches({
          ingredient_id: ingredientId,
          pageIndex: 0,
          pageSize: 100,
          sortField: "expiry_date",
          sortOrder: "asc",
          include_wasted: false,
        });

        console.log("Raw batches response:", response);

        // Handle both response formats
        const batchesList = response.data?.batches || response.batches || [];

        if (!batchesList || !Array.isArray(batchesList)) {
          console.warn("No valid batches array found in response:", response);
          return {
            batches: [],
            totalCost: 0,
            averageCost: 0,
            totalQuantity: 0,
          };
        }

        // Map the batches from the response to ExtendedBatch format
        const batches = batchesList.map((batch: any) => {
          console.log("Processing batch:", batch);
          return {
            id: batch._id,
            batch_id: batch._id,
            quantity: batch.quantity,
            unit: batch.unit,
            cost_per_unit: batch.cost_per_unit,
            total_ingredient_batch_cost: batch.total_cost,
            batch_number: batch.batch_number,
            remaining_quantity: batch.remaining_quantity,
            expiry_date: batch.expiry_date,
            storage_place_name: batch.warehouse_branch || "",
            storage_place_id: batch.warehouse_branch || "",
            product_id: batch.product_id,
            brand_name: batch.brand_name,
            storage_place: batch.warehouse_branch || "",
            source_name: batch.source_name || "",
            source_type: batch.source_type || "",
            notes: batch.notes || "",
            product_name: batch.brand_name || "",
            product_type: "",
            product_unit: batch.unit,
            product_cost_per_unit: batch.cost_per_unit,
            product_total_cost: batch.total_cost || 0,
            product_remaining_quantity: batch.remaining_quantity,
            product_expiry_date: batch.expiry_date,
            product_storage_place_name: batch.warehouse_branch || "",
            product_storage_place_id: batch.warehouse_branch || "",
            product_category: "",
            product_description: "",
            product_brand: batch.brand_name,
            product_model: "",
            product_sku: "",
            product_barcode: "",
            product_tags: [],
            product_attributes: {},
            product_metadata: {},
            product_created_at: batch.created_at || "",
            product_updated_at: batch.updated_at || "",
            created_at: batch.created_at || "",
            updated_at: batch.updated_at || "",
          };
        });

        console.log("Mapped batches:", batches);

        const totalCost = batches.reduce(
          (sum: number, batch: ExtendedBatch) =>
            sum + (batch.total_ingredient_batch_cost || 0),
          0
        );
        const totalQuantity = batches.reduce(
          (sum: number, batch: ExtendedBatch) =>
            sum + (batch.remaining_quantity || 0),
          0
        );
        const averageCost = totalQuantity > 0 ? totalCost / totalQuantity : 0;

        const batchInfo: BatchInfo = {
          batches,
          totalCost,
          averageCost,
          totalQuantity,
          lastFetched: Date.now(),
        };

        console.log("Final batchInfo:", batchInfo);

        setBatchDataCache((prev) => ({
          ...prev,
          [ingredientId]: batchInfo,
        }));

        return {
          batches: batchInfo.batches,
          totalCost: batchInfo.totalCost,
          averageCost: batchInfo.averageCost,
          totalQuantity: batchInfo.totalQuantity,
        };
      } catch (error) {
        console.error("Error loading batches:", error);
        toast.error("Failed to load batches");
        return {
          batches: [],
          totalCost: 0,
          averageCost: 0,
          totalQuantity: 0,
        };
      }
    },
    [batchDataCache]
  );

  useEffect(() => {
    loadIngredients();
  }, []);

  console.log(modifiedIngredients);

  // Validation function
  const validateIngredients = () => {
    if (isViewMode) return true;

    if (!modifiedIngredients.length) {
      return false;
    }

    const invalidIngredients = modifiedIngredients.filter(
      (ingredient: ModifiedIngredient) => {
        // Check if the ingredient has any selected batches
        if (!ingredient.selected_batches.length) {
          return true;
        }

        // Calculate total allocated quantity in ingredient's unit
        const totalAllocated = ingredient.selected_batches.reduce(
          (total, batch) => {
            const convertedQuantity =
              batch.unit !== ingredient.unit
                ? convertUnits(batch.quantity, batch.unit, ingredient.unit) || 0
                : batch.quantity;
            return total + convertedQuantity;
          },
          0
        );

        // Check if allocated quantity matches required quantity
        const difference = Math.abs(
          totalAllocated - ingredient.required_quantity
        );
        return difference > 0.0001; // Using small threshold for floating point comparison
      }
    );

    if (invalidIngredients.length > 0) {
      return false;
    }

    return true;
  };

  // Add useEffect for validation
  useEffect(() => {
    const isValid = validateIngredients();
    setCanProceed(isValid);
  }, [modifiedIngredients, setCanProceed]);

  // Update handleUpdateFullDoc to include validation
  const handleIngredientsUpdate = (
    updatedIngredients: ModifiedIngredient[]
  ) => {
    setModifiedIngredients(updatedIngredients);
    const isValid = validateIngredients();
    setCanProceed(isValid);

    // Only update the full document if validation passes
    if (isValid) {
      handleUpdateFullDoc(updatedIngredients, "ingredients");
    }
  };

  return (
    <Fragment>
      {isViewMode && <ViewModeMessage />}
      {ingredientsLoading ? (
        <Stack spacing={2}>
          <IngredientSkeleton />
          <IngredientSkeleton />
          <IngredientSkeleton />
        </Stack>
      ) : (
        <Box>
          {/* Recipe Multiplier Section */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: "white",
              borderRadius: 2,
              boxShadow: `0 1px 3px ${alpha("#000", 0.1)}`,
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#0891B2",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Icon fontSize="small">calculate</Icon>
                  Recipe Multiplier
                </Typography>
                {isViewMode ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      bgcolor: alpha(theme.palette.grey[100], 0.5),
                      border: `1px solid ${alpha(
                        theme.palette.grey[300],
                        0.8
                      )}`,
                    }}
                  >
                    <Icon sx={{ color: theme.palette.grey[600] }}>
                      straighten
                    </Icon>
                    <Typography>{multiplier}</Typography>
                  </Paper>
                ) : (
                  <TextField
                    type="number"
                    value={multiplier}
                    onChange={(e) => setMultiplier(Number(e.target.value))}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon sx={{ color: "#0891B2" }}>straighten</Icon>
                        </InputAdornment>
                      ),
                      inputProps: { min: 0.1, step: 0.1 },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "white",
                        "&:hover": {
                          bgcolor: alpha("#0891B2", 0.02),
                        },
                        "&.Mui-focused": {
                          bgcolor: "white",
                          boxShadow: `0 0 0 2px ${alpha("#0891B2", 0.2)}`,
                        },
                      },
                    }}
                  />
                )}
              </Grid>
              {!isViewMode && (
                <Grid item xs={12} md={6}>
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent={{ xs: "flex-start", md: "flex-end" }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<Icon>auto_awesome</Icon>}
                      onClick={handleAutoAllocateAll}
                      disabled={autoAllocating}
                      sx={{
                        borderColor: alpha("#0891B2", 0.3),
                        color: "#0891B2",
                        "&:hover": {
                          borderColor: "#0891B2",
                          bgcolor: alpha("#0891B2", 0.04),
                        },
                      }}
                    >
                      {autoAllocating ? "Allocating..." : "Auto Allocate"}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Icon>add</Icon>}
                      onClick={handleOpenAddDialog}
                      sx={{
                        bgcolor: "#0891B2",
                        "&:hover": {
                          bgcolor: "#0E7490",
                        },
                      }}
                    >
                      Add Ingredient
                    </Button>
                  </Stack>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Ingredients List */}
          <Stack spacing={2}>
            {modifiedIngredients.map(
              (ingredient: ModifiedIngredient, index: number) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: "white",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: isViewMode
                      ? alpha(theme.palette.grey[300], 0.8)
                      : isQuantityModified(ingredient)
                      ? alpha("#F59E0B", 0.5)
                      : alpha("#0891B2", 0.1),
                    boxShadow: isViewMode
                      ? "none"
                      : isQuantityModified(ingredient)
                      ? `0 0 0 1px ${alpha("#F59E0B", 0.1)}`
                      : "none",
                  }}
                >
                  {/* Ingredient Header */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: isViewMode
                          ? alpha(theme.palette.grey[100], 0.8)
                          : alpha("#0891B2", 0.08),
                        color: isViewMode ? theme.palette.grey[600] : "#0891B2",
                      }}
                    >
                      <Icon>inventory</Icon>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          fontFamily: "Montserrat",
                          color: isViewMode
                            ? theme.palette.grey[700]
                            : "inherit",
                        }}
                      >
                        {ingredient.brand_name}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {!isViewMode && isQuantityModified(ingredient) && (
                          <Chip
                            size="small"
                            label="Modified"
                            sx={{
                              bgcolor: alpha("#F59E0B", 0.1),
                              color: "#92400E",
                              height: 20,
                            }}
                          />
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            color: isViewMode
                              ? theme.palette.grey[600]
                              : "text.secondary",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Icon sx={{ fontSize: 16 }}>
                            {isViewMode
                              ? "check_circle"
                              : ingredient.remaining_quantity > 0
                              ? "warning"
                              : "check_circle"}
                          </Icon>
                          {isViewMode
                            ? `${ingredient.modified_quantity} ${ingredient.unit}`
                            : ingredient.remaining_quantity > 0
                            ? `${ingredient.remaining_quantity} ${ingredient.unit} remaining`
                            : "Fully allocated"}
                        </Typography>
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      {!isViewMode && (
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveIngredient(index)}
                          sx={{
                            color: "error.main",
                            "&:hover": {
                              bgcolor: alpha("#DC2626", 0.04),
                            },
                          }}
                        >
                          <Icon>delete</Icon>
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleExpandIngredient(index)}
                        sx={{
                          color: isViewMode
                            ? theme.palette.grey[400]
                            : "#0891B2",
                          transform: expandedIngredients[index]
                            ? "rotate(180deg)"
                            : "none",
                          transition: "transform 0.2s",
                        }}
                      >
                        <Icon>expand_more</Icon>
                      </IconButton>
                    </Stack>
                  </Box>

                  {/* Quantity Controls */}
                  <Box sx={{ mb: expandedIngredients[index] ? 2 : 0 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={6}>
                        {isViewMode ? (
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              bgcolor: alpha(theme.palette.grey[100], 0.5),
                              border: `1px solid ${alpha(
                                theme.palette.grey[300],
                                0.8
                              )}`,
                            }}
                          >
                            <Icon sx={{ color: theme.palette.grey[600] }}>
                              straighten
                            </Icon>
                            <Typography>
                              {ingredient.modified_quantity} {ingredient.unit}
                            </Typography>
                          </Paper>
                        ) : (
                          <TextField
                            type="number"
                            value={ingredient.modified_quantity}
                            onChange={(e) =>
                              handleIngredientQuantityChange(
                                index,
                                Number(e.target.value)
                              )
                            }
                            fullWidth
                            label="Quantity"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Icon sx={{ color: "#0891B2" }}>
                                    straighten
                                  </Icon>
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <CompactUnitSelect
                                  value={ingredient.unit}
                                  onChange={(value) =>
                                    handleUnitChange(index, value)
                                  }
                                  quantity={ingredient.modified_quantity}
                                />
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                bgcolor: "white",
                                "&:hover": {
                                  bgcolor: alpha("#0891B2", 0.02),
                                },
                                "&.Mui-focused": {
                                  bgcolor: "white",
                                  boxShadow: `0 0 0 2px ${alpha(
                                    "#0891B2",
                                    0.2
                                  )}`,
                                },
                              },
                            }}
                          />
                        )}
                      </Grid>
                      {!isViewMode && (
                        <Grid item xs={12} md={6}>
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >
                            <Chip
                              label={`Required: ${ingredient.required_quantity} ${ingredient.unit}`}
                              size="small"
                              sx={{
                                bgcolor: alpha("#0891B2", 0.04),
                                color: "#0891B2",
                              }}
                            />
                            {isQuantityModified(ingredient) && (
                              <Chip
                                label={`Original: ${ingredient.original_quantity} ${ingredient.unit}`}
                                size="small"
                                sx={{
                                  bgcolor: alpha("#F59E0B", 0.04),
                                  color: "#92400E",
                                }}
                              />
                            )}
                          </Stack>
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  {/* Batches Section */}
                  {expandedIngredients[index] && (
                    <Fade in={expandedIngredients[index]}>
                      <Box>
                        <Divider sx={{ my: 2 }} />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 2,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: isViewMode
                                ? theme.palette.grey[600]
                                : "#0891B2",
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Icon fontSize="small">inventory_2</Icon>
                            {isViewMode ? "Used Batch" : "Available Batches"}
                          </Typography>
                          {!isViewMode && (
                            <Button
                              size="small"
                              startIcon={<Icon>auto_awesome</Icon>}
                              onClick={() =>
                                autoAllocateBatches(index, ingredient)
                              }
                              sx={{
                                color: "#0891B2",
                                "&:hover": {
                                  bgcolor: alpha("#0891B2", 0.04),
                                },
                              }}
                            >
                              Auto Allocate
                            </Button>
                          )}
                        </Box>

                        {ingredientsLoading ? (
                          <Stack spacing={2}>
                            <BatchSkeleton />
                            <BatchSkeleton />
                          </Stack>
                        ) : (
                          <Stack spacing={2}>
                            {(isViewMode
                              ? ingredient.selected_batches
                              : ingredient.batches
                            )?.map((batch: any, batchIndex: number) => {
                              // Find the selected batch data for this specific batch
                              const selectedBatch =
                                ingredient.selected_batches.find(
                                  (sb) => sb.id === batch.batch_id
                                );

                              const isExpired =
                                !isViewMode &&
                                dayjs(batch.expiry_date).isBefore(dayjs());
                              const isNearExpiry =
                                !isViewMode &&
                                dayjs(batch.expiry_date).isBefore(
                                  dayjs().add(7, "day")
                                );
                              const availabilityPercentage = isViewMode
                                ? 0
                                : (batch.remaining_quantity / batch.quantity) *
                                  100;
                              const isLowStock =
                                !isViewMode && availabilityPercentage < 20;
                              const isMediumStock =
                                !isViewMode && availabilityPercentage < 50;

                              return (
                                <Paper
                                  key={`${batch.batch_id}-${batchIndex}`}
                                  variant="outlined"
                                  sx={{
                                    position: "relative",
                                    p: 2,
                                    bgcolor: isViewMode
                                      ? alpha(theme.palette.grey[100], 0.5)
                                      : selectedBatch
                                      ? alpha("#0891B2", 0.04)
                                      : "white",
                                    borderColor: isViewMode
                                      ? alpha(theme.palette.grey[300], 0.8)
                                      : selectedBatch
                                      ? "#0891B2"
                                      : alpha("#0891B2", 0.1),
                                    borderRadius: 1,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      borderColor: isViewMode
                                        ? alpha(theme.palette.grey[300], 0.8)
                                        : "#0891B2",
                                      boxShadow: isViewMode
                                        ? "none"
                                        : `0 0 0 1px ${alpha("#0891B2", 0.1)}`,
                                    },
                                  }}
                                >
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} md={isViewMode ? 6 : 4}>
                                      <Stack spacing={1}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <Stack>
                                            <Typography
                                              variant="subtitle2"
                                              sx={{
                                                fontWeight: 600,
                                                color: isViewMode
                                                  ? theme.palette.grey[700]
                                                  : "inherit",
                                              }}
                                            >
                                              {batch?.brand_name || "-"}
                                            </Typography>
                                            <Typography
                                              variant="subtitle2"
                                              sx={{
                                                fontWeight: 400,
                                                fontSize: 12,
                                                color: "grey",
                                              }}
                                            >
                                              {batch.batch_number}
                                            </Typography>
                                          </Stack>
                                        </Box>
                                        {!isViewMode && (
                                          <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                          >
                                            <Icon
                                              sx={{
                                                fontSize: 16,
                                                color: isExpired
                                                  ? "#DC2626"
                                                  : isNearExpiry
                                                  ? "#F59E0B"
                                                  : "#0891B2",
                                              }}
                                            >
                                              {isExpired
                                                ? "error"
                                                : isNearExpiry
                                                ? "warning"
                                                : "event"}
                                            </Icon>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: isExpired
                                                  ? "#991B1B"
                                                  : isNearExpiry
                                                  ? "#92400E"
                                                  : "text.secondary",
                                              }}
                                            >
                                              Expires:{" "}
                                              {dayjs(batch.expiry_date).format(
                                                "MMM D, YYYY"
                                              )}
                                            </Typography>
                                          </Stack>
                                        )}
                                      </Stack>
                                    </Grid>

                                    <Grid item xs={12} md={isViewMode ? 6 : 4}>
                                      <Stack spacing={1}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: isViewMode
                                              ? theme.palette.grey[600]
                                              : "text.secondary",
                                          }}
                                        >
                                          {isViewMode
                                            ? "Used Quantity"
                                            : "Available Quantity"}
                                        </Typography>
                                        {isViewMode ? (
                                          <Typography
                                            variant="body1"
                                            sx={{
                                              fontWeight: 500,
                                              color: theme.palette.grey[700],
                                            }}
                                          >
                                            {batch.quantity} {batch.unit}
                                          </Typography>
                                        ) : (
                                          <>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontWeight: 600,
                                                color: isLowStock
                                                  ? "#991B1B"
                                                  : isMediumStock
                                                  ? "#92400E"
                                                  : "#0891B2",
                                              }}
                                            >
                                              {batch.remaining_quantity}/
                                              {batch.quantity} {batch.unit}
                                            </Typography>
                                            <Box sx={{ position: "relative" }}>
                                              <LinearProgress
                                                variant="determinate"
                                                value={
                                                  isViewMode
                                                    ? 0
                                                    : availabilityPercentage
                                                }
                                                sx={{
                                                  height: 16,
                                                  borderRadius: 4,
                                                  bgcolor: alpha(
                                                    "#0891B2",
                                                    0.1
                                                  ),
                                                  "& .MuiLinearProgress-bar": {
                                                    bgcolor: isLowStock
                                                      ? "#DC2626"
                                                      : isMediumStock
                                                      ? "#F59E0B"
                                                      : "#0891B2",
                                                    borderRadius: 4,
                                                  },
                                                }}
                                              />
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  position: "absolute",
                                                  top: "50%",
                                                  left: "50%",
                                                  transform:
                                                    "translate(-50%, -50%)",
                                                  color: "text.secondary",
                                                  fontWeight: 600,
                                                  textShadow: "0 0 4px white",
                                                }}
                                              >
                                                {isViewMode
                                                  ? 0
                                                  : Math.round(
                                                      availabilityPercentage
                                                    )}
                                                %
                                              </Typography>
                                            </Box>
                                          </>
                                        )}
                                      </Stack>
                                    </Grid>

                                    {!isViewMode ? (
                                      <Grid item xs={12} md={4}>
                                        <Stack spacing={1}>
                                          <Typography
                                            variant="caption"
                                            sx={{ color: "text.secondary" }}
                                          >
                                            Allocate from this batch
                                          </Typography>
                                          <TextField
                                            type="number"
                                            size="small"
                                            value={
                                              selectedBatch?.quantity || ""
                                            }
                                            onChange={(e) => {
                                              const newQuantity = Number(
                                                e.target.value
                                              );
                                              if (!isNaN(newQuantity)) {
                                                handleBatchSelect(
                                                  index,
                                                  batch.batch_id,
                                                  newQuantity,
                                                  selectedBatch?.unit ||
                                                    batch.unit
                                                );
                                              }
                                            }}
                                            placeholder="Enter quantity"
                                            fullWidth
                                            InputProps={{
                                              endAdornment: (
                                                <CompactUnitSelect
                                                  value={
                                                    selectedBatch?.unit ||
                                                    batch.unit
                                                  }
                                                  onChange={(newUnit) => {
                                                    const currentQuantity =
                                                      selectedBatch?.quantity ||
                                                      0;
                                                    const currentUnit =
                                                      selectedBatch?.unit ||
                                                      batch.unit;
                                                    handleBatchUnitChange(
                                                      index,
                                                      batch.batch_id,
                                                      currentQuantity,
                                                      currentUnit,
                                                      newUnit
                                                    );
                                                  }}
                                                  quantity={
                                                    selectedBatch?.quantity || 0
                                                  }
                                                  sourceUnit={ingredient.unit}
                                                />
                                              ),
                                              inputProps: {
                                                min: 0,
                                                step: 0.1,
                                              },
                                            }}
                                            sx={{
                                              "& .MuiOutlinedInput-root": {
                                                bgcolor: "white",
                                                "&:hover": {
                                                  bgcolor: alpha(
                                                    "#0891B2",
                                                    0.02
                                                  ),
                                                },
                                                "&.Mui-focused": {
                                                  bgcolor: "white",
                                                  boxShadow: `0 0 0 2px ${alpha(
                                                    "#0891B2",
                                                    0.2
                                                  )}`,
                                                },
                                              },
                                            }}
                                          />
                                          {selectedBatch && (
                                            <Button
                                              size="small"
                                              startIcon={
                                                <Icon>remove_circle</Icon>
                                              }
                                              onClick={() =>
                                                handleBatchSelect(
                                                  index,
                                                  batch.batch_id,
                                                  0,
                                                  batch.unit
                                                )
                                              }
                                              sx={{
                                                color: "#991B1B",
                                                "&:hover": {
                                                  bgcolor: alpha(
                                                    "#DC2626",
                                                    0.04
                                                  ),
                                                },
                                              }}
                                            >
                                              Remove Allocation
                                            </Button>
                                          )}
                                        </Stack>
                                      </Grid>
                                    ) : null}
                                  </Grid>
                                </Paper>
                              );
                            })}
                          </Stack>
                        )}
                      </Box>
                    </Fade>
                  )}
                </Paper>
              )
            )}
          </Stack>
        </Box>
      )}
      {!isViewMode &&
        modifiedIngredients.some((ingredient: ModifiedIngredient) => {
          const totalAllocated = ingredient.selected_batches.reduce(
            (total, batch) => {
              const convertedQuantity =
                batch.unit !== ingredient.unit
                  ? convertUnits(batch.quantity, batch.unit, ingredient.unit) ||
                    0
                  : batch.quantity;
              return total + convertedQuantity;
            },
            0
          );
          return (
            Math.abs(totalAllocated - ingredient.required_quantity) > 0.0001
          );
        }) && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              bgcolor: alpha(theme.palette.error.light, 0.1),
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Icon sx={{ color: theme.palette.error.main }}>error</Icon>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.error.main }}
            >
              Cannot proceed: Some ingredients have incomplete batch
              allocations. Please ensure all ingredients have their required
              quantities allocated from available batches.
            </Typography>
          </Paper>
        )}
      <IngredientDialog
        open={itemDialogOpen}
        onClose={() => setItemDialogOpen(false)}
        onSave={(entries) => {
          const selectedIngredient = entries[0];
          if (!selectedIngredient) return;

          const selectedBatch = selectedIngredient.selectedBatch as
            | ExtendedBatch
            | undefined;

          const newIngredient: ModifiedIngredient = {
            item_id: selectedIngredient.ingredient.id,
            ingredient_id: selectedIngredient.ingredient.ingredient_id,
            brand_name: selectedIngredient.ingredient.name,
            modified_quantity: selectedIngredient.quantity,
            original_quantity: selectedIngredient.quantity,
            unit: selectedIngredient.unit,
            selected_batches: selectedBatch
              ? [
                  {
                    id: selectedBatch.batch_id,
                    quantity: selectedIngredient.quantity,
                    unit: selectedIngredient.unit,
                    cost_per_unit: selectedBatch.cost_per_unit,
                    total_ingredient_batch_cost:
                      selectedIngredient.quantity * selectedBatch.cost_per_unit,
                  },
                ]
              : [],
            is_modified: false,
            required_quantity: selectedIngredient.quantity,
            remaining_quantity: selectedIngredient.quantity,
            batches: selectedBatch ? [selectedBatch] : [],
          };

          setModifiedIngredients((prev: ModifiedIngredient[]) => {
            if (editingIngredientIndex !== null) {
              return prev.map((ing, i) =>
                i === editingIngredientIndex ? newIngredient : ing
              );
            }
            return [...prev, newIngredient];
          });
          setItemDialogOpen(false);
        }}
        editData={
          editingIngredientIndex !== null
            ? {
                ingredient: {
                  ...ingredientOptions.find(
                    (opt) =>
                      opt.ingredient_id ===
                      modifiedIngredients[editingIngredientIndex].ingredient_id
                  )!,
                  type: "ingredient",
                },
                quantity:
                  modifiedIngredients[editingIngredientIndex].modified_quantity,
                unit: modifiedIngredients[editingIngredientIndex].unit,
                notes: "",
                selectedBatch: modifiedIngredients[editingIngredientIndex]
                  .selected_batches[0] as ExtendedBatch,
              }
            : undefined
        }
        ingredientOptions={ingredientOptions.map((opt) => ({
          ...opt,
          type: "ingredient",
        }))}
        loadingIngredients={loadingIngredients}
        loadBatches={getBatchData}
        mode={editingIngredientIndex !== null ? "edit" : "add"}
        currentRecipeIngredients={modifiedIngredients}
      />
    </Fragment>
  );
};
